import React from 'react';

import { useAppSelector } from '@/store';
import { selectCreativeJobsMap, type CreativeJob } from '@/store/slices/creativeJobs';

import { listPendingOf } from '../../website/brand-settled';
import { useBrandSettled } from '../../website/useBrandSettled';
import { deleteBannerVersion, purgeBannerVersion, restoreBannerVersion } from '../creative-api';
import { keepPendingVersions } from '../ad-versions';

/** The fields every ad-library entity (banner kit, mini-game) shares with the hook. */
export interface AdLibraryEntity {
  id: string;
  archived: boolean;
  job_id?: string;
}

export interface UseAdLibraryOptions<T extends AdLibraryEntity> {
  /** Reads the tenant's list. Throws on an unreachable backend (the last list stays on screen). May be an inline
   *  function: it is read through a ref, so a new identity never re-fetches. */
  list: (tenantId: number, signal?: AbortSignal) => Promise<T[]>;
  /** The job kinds whose progress changes this list (e.g. `banner_kit`, `kit_layers`). */
  doneKinds: readonly string[];
}

export interface UseAdLibrary<T extends AdLibraryEntity> {
  /** The CURRENT brand's entities: empty until its first list lands (never the previous brand's rows after a switch,
   *  which a screen would show or an editor would open under the new brand). */
  items: T[];
  loading: boolean;
  /** The current brand's list has answered (success OR failure), or no brand will resolve: a screen shows its
   *  skeleton until this, then the list, its empty state or its failure. Unlike `loading`, a re-read of the same
   *  brand never turns it back off. */
  settled: boolean;
  /** The list for the CURRENT tenant has settled at least once (success OR failure), so an absent id is a real
   *  miss, not a not-yet-loaded one (a deep-linked editor must not 404 before the fetch). */
  loaded: boolean;
  /** The newest read for the CURRENT tenant failed (an unreachable backend, not an abort): an absent id is then
   *  unknown, not a miss. */
  listFailed: boolean;
  refresh: () => void;
  /** Archive an entity (optimistic; refetches to roll back a failure). */
  remove: (id: string) => Promise<boolean>;
  /** Recover an archived entity. */
  restore: (id: string) => Promise<boolean>;
  /** PERMANENTLY delete an archived entity (irreversible). */
  purge: (id: string) => Promise<boolean>;
  /** Optimistically merge fields into a cached entity (an in-editor edit), so a reopen or the card reads the fresh
   *  value while the debounced server write is still in flight. */
  patch: (id: string, fields: Partial<T>) => void;
}

/** The list handed out while the current brand has none of its own yet (stable, so consumers' memos hold). */
const NO_ITEMS: never[] = [];

/** What of a job makes the list stale: its progress count and whether it has finished. */
type JobMark = { completed: number; terminal: boolean };

const isTerminal = (job: CreativeJob): boolean => job.status === 'done' || job.status === 'failed';

/** The marks of this tenant's jobs of the given kinds (id -> mark). Pure. */
const jobMarks = (
  jobs: Record<string, CreativeJob>,
  tenantId: number | undefined,
  kinds: ReadonlySet<string>,
): Map<string, JobMark> => {
  const out = new Map<string, JobMark>();
  if (tenantId === undefined) return out;
  for (const job of Object.values(jobs)) {
    if (!job.kind || !kinds.has(job.kind) || job.tenant_id !== String(tenantId)) continue;
    out.set(job.id, { completed: job.completed ?? 0, terminal: isTerminal(job) });
  }
  return out;
};

/** The KNOWN jobs that just finished (not terminal before, terminal now). Pure. */
const jobsEnded = (prev: Map<string, JobMark>, next: Map<string, JobMark>): string[] => {
  const out: string[] = [];
  for (const [id, mark] of next) {
    const before = prev.get(id);
    if (before && !before.terminal && mark.terminal) out.push(id);
  }
  return out;
};

/** Whether a KNOWN job moved on (its progress count changed, or it just finished). A job first seen is not a
 *  change: the list already reflects it (an optimistic enqueue, or the rehydrate of past runs). Pure. */
const jobsMovedOn = (prev: Map<string, JobMark>, next: Map<string, JobMark>): boolean => {
  for (const [id, mark] of next) {
    const before = prev.get(id);
    if (!before) continue;
    if (before.completed !== mark.completed || (!before.terminal && mark.terminal)) return true;
  }
  return false;
};

/**
 * Loads a brand's ad entities (banner kits, mini-games) and keeps them fresh: refetches when the tenant changes and
 * whenever one of its jobs of `doneKinds` lands a result (its `completed` count moves, so a kit's size grid fills
 * shape by shape) or finishes. Archive / restore / purge reuse the banner-version endpoints, which archive any
 * creatives row by id. Generalised from `useVideoLibrary`.
 */
export const useAdLibrary = <T extends AdLibraryEntity>(
  tenantId: number | undefined,
  options: UseAdLibraryOptions<T>,
): UseAdLibrary<T> => {
  const [items, setItems] = React.useState<T[]>([]);
  // The tenant `items` were read for: they are handed out only for it.
  const [itemsFor, setItemsFor] = React.useState<number | undefined>(undefined);
  const [loading, setLoading] = React.useState(false);
  const brandSettled = useBrandSettled();
  // The tenant whose list has settled (success OR failure): tells "not fetched yet" from "fetched, not there".
  const [loadedFor, setLoadedFor] = React.useState<number | undefined>(undefined);
  // The tenant whose newest read failed (cleared by a read that lands).
  const [failedFor, setFailedFor] = React.useState<number | undefined>(undefined);
  // Monotonic guard: a slow in-flight list for a previous tenant must never overwrite a newer one.
  const reqRef = React.useRef(0);
  // The brand this render is for. A load() kept by a closure from before a brand switch (a late save's refresh, an
  // archive rollback, a reconcile's re-read) must not start a read: it would bump reqRef past the current brand's read
  // in flight, which then never settles, and the grid would stay on its skeleton.
  const tenantRef = React.useRef(tenantId);
  tenantRef.current = tenantId;
  // The list reader through a ref: callers pass a fresh options object per render, and a load keyed on it would
  // re-fetch in a loop.
  const listRef = React.useRef(options.list);
  listRef.current = options.list;
  // The jobs that finished since the last read landed: on the next read their entities' drafts take the server's
  // document (keepPendingVersions' serverWins), which the run wrote after the editor's last save.
  const endedRef = React.useRef<Set<string>>(new Set());

  const load = React.useCallback(
    (signal?: AbortSignal): void => {
      if (tenantId !== tenantRef.current) return; // a superseded brand's load (see tenantRef)
      if (!tenantId) {
        // No brand to read for (a brand still being created): forget the previous brand's read entirely, so coming
        // back to it is pending again (its skeleton) instead of settled over an emptied list; a read still in flight
        // can no longer settle it.
        reqRef.current += 1;
        setItems([]);
        setItemsFor(undefined);
        setLoadedFor(undefined);
        setFailedFor(undefined);
        setLoading(false);
        return;
      }
      const seq = (reqRef.current += 1);
      setLoading(true);
      void listRef
        .current(tenantId, signal)
        .then((rows) => {
          if (seq !== reqRef.current) return;
          const ended = endedRef.current;
          endedRef.current = new Set();
          setItems((prev) =>
            keepPendingVersions(prev, rows, ended.size ? (row) => Boolean(row.job_id && ended.has(row.job_id)) : undefined),
          );
          setItemsFor(tenantId);
          setFailedFor(undefined);
        })
        .catch(() => {
          // A transient failure (or an abort) keeps the last list on screen; only a real failure is marked.
          if (seq === reqRef.current && !signal?.aborted) setFailedFor(tenantId);
        })
        .finally(() => {
          if (seq === reqRef.current) {
            setLoading(false);
            setLoadedFor(tenantId); // settle on failure too, so a failed list never hangs an editor on "Loading"
          }
        });
    },
    [tenantId],
  );

  React.useEffect(() => {
    const controller = new AbortController();
    load(controller.signal);
    return () => controller.abort();
  }, [load]);

  // Refetch when one of this tenant's relevant jobs moves on: the server writes each landed render (and the final
  // lock) onto the entity, so the list must pick it up without a reload. Keyed by the kinds' text so a fresh
  // array literal per render does not count as a change.
  const kindsKey = options.doneKinds.join(',');
  const kinds = React.useMemo(() => new Set(kindsKey.split(',').filter(Boolean)), [kindsKey]);
  const jobsById = useAppSelector(selectCreativeJobsMap);
  const marks = React.useMemo(() => jobMarks(jobsById, tenantId, kinds), [jobsById, tenantId, kinds]);
  const prevMarksRef = React.useRef(marks);
  React.useEffect(() => {
    const moved = jobsMovedOn(prevMarksRef.current, marks);
    for (const id of jobsEnded(prevMarksRef.current, marks)) endedRef.current.add(id);
    prevMarksRef.current = marks;
    if (moved) load();
  }, [marks, load]);

  const remove = React.useCallback(
    async (id: string): Promise<boolean> => {
      if (!tenantId) return false;
      // Optimistic: MARK archived (it leaves the live grid for the Archived sheet) rather than a success-path
      // refetch, which can race the server's write and resurrect it (the useVideoLibrary lesson).
      setItems((prev) => prev.map((v) => (v.id === id ? { ...v, archived: true } : v)));
      const ok = await deleteBannerVersion(tenantId, id);
      if (!ok) load(); // rollback: refetch the true state
      return ok;
    },
    [tenantId, load],
  );

  const restore = React.useCallback(
    async (id: string): Promise<boolean> => {
      if (!tenantId) return false;
      setItems((prev) => prev.map((v) => (v.id === id ? { ...v, archived: false } : v)));
      const ok = await restoreBannerVersion(tenantId, id);
      if (!ok) load(); // rollback
      return ok;
    },
    [tenantId, load],
  );

  const purge = React.useCallback(
    async (id: string): Promise<boolean> => {
      if (!tenantId) return false;
      setItems((prev) => prev.filter((v) => v.id !== id)); // optimistic (irreversible on the server)
      const ok = await purgeBannerVersion(tenantId, id);
      if (!ok) load(); // rollback
      return ok;
    },
    [tenantId, load],
  );

  const patch = React.useCallback((id: string, fields: Partial<T>): void => {
    setItems((prev) => prev.map((v) => (v.id === id ? { ...v, ...fields } : v)));
  }, []);

  const refresh = React.useCallback((): void => load(), [load]);

  const pending = listPendingOf(tenantId, loadedFor, brandSettled);
  return {
    items: tenantId !== undefined && itemsFor === tenantId ? items : NO_ITEMS,
    // Loading until this brand's list has answered, not only while a read is in flight: before the brand (or the
    // first read) lands, an empty list would otherwise show its "none yet" state for a moment.
    loading: loading || pending,
    settled: !pending,
    loaded: tenantId !== undefined && loadedFor === tenantId,
    listFailed: tenantId !== undefined && failedFor === tenantId,
    refresh,
    remove,
    restore,
    purge,
    patch,
  };
};
